import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchAllNotices, fetchNoticeDetail, createNotice, updateNotice, deleteNotice } from '../../services/api';
import './NoticeManagementPage.css';

function NoticeManagementPage() {
  const location = useLocation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [noticeToDelete, setNoticeToDelete] = useState(null);
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingOpenNoticeId, setPendingOpenNoticeId] = useState(null);

  // 페이지네이션 상태
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  // 공지사항 목록 로드
  useEffect(() => {
    // 헤더에서 넘어온 공지사항 ID 저장
    if (location.state?.openNoticeId) {
      setPendingOpenNoticeId(location.state.openNoticeId);
    }
    loadNotices();
  }, [location.state]);

  const getNoticeStatus = (notice) => {
    const now = new Date();
    const { startDate, endDate, isActive, isPinned } = notice;

    // 비활성화된 경우
    if (!isActive) {
      return '비활성';
    }

    // 종료일이 지난 경우
    if (endDate && new Date(endDate) < now) {
      return '종료';
    }

    // 시작일이 아직 안 된 경우
    if (startDate && new Date(startDate) > now) {
      return '예정';
    }

    // 활성 + 상단고정 + 기간 내 = 게시중
    if (isActive && isPinned) {
      return '게시중';
    }

    // 활성이지만 상단고정 아님
    return '대기';
  };

  const loadNotices = async () => {
    try {
      setLoading(true);
      const data = await fetchAllNotices();
      // API 데이터를 프론트 형식으로 변환
      const formattedNotices = data.map(notice => {
        const noticeData = {
          id: notice.id,
          title: notice.title,
          content: notice.content || '',
          category: notice.category || '공지',
          author: '관리자',
          views: notice.viewCount || 0,
          createdAt: notice.createdAt,
          priority: notice.type === 'urgent' ? '높음' : notice.type === 'update' ? '보통' : '보통',
          isPinned: notice.isNotice || false,
          isActive: notice.isActive !== false,
          startDate: notice.startDate,
          endDate: notice.endDate,
          noticeType: notice.type || 'notice'
        };
        noticeData.status = getNoticeStatus(noticeData);
        return noticeData;
      });
      setNotices(formattedNotices);
    } catch (error) {
      console.error('공지사항 로드 실패:', error);
      // 에러 시 샘플 데이터 표시
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('전체');
  const [filterStatus, setFilterStatus] = useState('전체');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [newNotice, setNewNotice] = useState({
    title: '',
    content: '',
    category: '일반',
    priority: '보통',
    isPinned: false,
    startDate: '',
    endDate: ''
  });
  const [editNotice, setEditNotice] = useState({
    id: null,
    title: '',
    content: '',
    category: '일반',
    priority: '보통',
    isPinned: false,
    startDate: '',
    endDate: ''
  });

  // 헤더에서 클릭한 공지사항 모달 열기
  useEffect(() => {
    if (pendingOpenNoticeId && notices.length > 0) {
      const noticeToOpen = notices.find(n => n.id === pendingOpenNoticeId);
      if (noticeToOpen) {
        handleNoticeClick(noticeToOpen);
        setPendingOpenNoticeId(null);
      }
    }
  }, [pendingOpenNoticeId, notices]);

  const handleNoticeClick = async (notice) => {
    // 조회수 증가 API 호출
    try {
      await fetchNoticeDetail(notice.id);
      // 로컬 상태 업데이트
      setNotices(notices.map(n =>
        n.id === notice.id ? { ...n, views: n.views + 1 } : n
      ));
    } catch (error) {
      console.error('조회수 증가 실패:', error);
    }
    // 수정 모달 열기
    handleOpenEditModal(notice);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedNotice(null);
  };

  const handleDeleteClick = (notice) => {
    setNoticeToDelete(notice);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteNotice(noticeToDelete.id);
      alert(`"${noticeToDelete.title}" 공지가 삭제되었습니다.`);
      setNotices(notices.filter(n => n.id !== noticeToDelete.id));
      setIsDeleteModalOpen(false);
      setNoticeToDelete(null);
    } catch (error) {
      console.error('공지 삭제 실패:', error);
      alert('공지 삭제에 실패했습니다.');
    }
  };

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setNewNotice({
      title: '',
      content: '',
      category: '일반',
      priority: '보통',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setNewNotice({
      title: '',
      content: '',
      category: '일반',
      priority: '보통',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const handleInputChange = (field, value) => {
    setNewNotice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field, value) => {
    setEditNotice(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16);
  };

  const handleOpenEditModal = (notice) => {
    setEditNotice({
      id: notice.id,
      title: notice.title,
      content: notice.content,
      category: notice.category,
      priority: notice.priority,
      isPinned: notice.isPinned,
      startDate: formatDateForInput(notice.startDate),
      endDate: formatDateForInput(notice.endDate)
    });
    setIsModalOpen(false);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditNotice({
      id: null,
      title: '',
      content: '',
      category: '일반',
      priority: '보통',
      isPinned: false,
      startDate: '',
      endDate: ''
    });
  };

  const handleUpdateNotice = async () => {
    if (!editNotice.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    if (!editNotice.content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    // 날짜 범위 검증
    if (editNotice.startDate && editNotice.endDate) {
      if (new Date(editNotice.endDate) < new Date(editNotice.startDate)) {
        alert('종료일은 시작일 이후여야 합니다.');
        return;
      }
    }

    try {
      const noticeType = editNotice.priority === '높음' ? 'urgent' :
                         editNotice.priority === '보통' ? 'update' : 'notice';

      const requestData = {
        title: editNotice.title,
        content: editNotice.content,
        category: editNotice.category,
        noticeType: noticeType,
        isNotice: editNotice.isPinned,
        priority: editNotice.priority === '높음' ? 2 : editNotice.priority === '보통' ? 1 : 0,
        startDate: editNotice.startDate || null,
        endDate: editNotice.endDate || null
      };

      await updateNotice(editNotice.id, requestData);
      handleCloseEditModal();
      alert('공지사항이 성공적으로 수정되었습니다.');
      loadNotices();
    } catch (error) {
      console.error('공지사항 수정 실패:', error);
      alert('공지사항 수정에 실패했습니다.');
    }
  };

  const handleAddNotice = async () => {
    // 제목 필수 입력 검사
    if (!newNotice.title.trim()) {
      alert('제목을 입력해주세요.');
      return;
    }

    // 내용 필수 입력 검사
    if (!newNotice.content.trim()) {
      alert('내용을 입력해주세요.');
      return;
    }

    // 날짜 범위 검증
    if (newNotice.startDate && newNotice.endDate) {
      if (new Date(newNotice.endDate) < new Date(newNotice.startDate)) {
        alert('종료일은 시작일 이후여야 합니다.');
        return;
      }
    }

    try {
      // 우선순위를 noticeType으로 변환
      const noticeType = newNotice.priority === '높음' ? 'urgent' :
                         newNotice.priority === '보통' ? 'update' : 'notice';

      const requestData = {
        title: newNotice.title,
        content: newNotice.content,
        category: newNotice.category,
        noticeType: noticeType,
        isNotice: newNotice.isPinned,
        isPush: false,
        isActive: true,
        priority: newNotice.priority === '높음' ? 2 : newNotice.priority === '보통' ? 1 : 0,
        startDate: newNotice.startDate || null,
        endDate: newNotice.endDate || null
      };

      await createNotice(requestData);
      handleCloseAddModal();
      alert('공지사항이 성공적으로 등록되었습니다.');
      loadNotices(); // 목록 새로고침
    } catch (error) {
      console.error('공지사항 등록 실패:', error);
      alert('공지사항 등록에 실패했습니다.');
    }
  };

  const filteredNotices = useMemo(() => {
    return notices
      .filter(notice => {
        const categoryMatch = filterCategory === '전체' || notice.category === filterCategory;
        const statusMatch = filterStatus === '전체' || notice.status === filterStatus;
        const searchMatch = searchKeyword === '' ||
          notice.title.toLowerCase().includes(searchKeyword.toLowerCase()) ||
          notice.content.toLowerCase().includes(searchKeyword.toLowerCase());
        return categoryMatch && statusMatch && searchMatch;
      })
      .sort((a, b) => {
        // 작성일 기준 내림차순 정렬
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });
  }, [notices, filterCategory, filterStatus, searchKeyword]);

  // 페이지네이션 적용
  const paginatedNotices = useMemo(() => {
    const startIndex = page * size;
    return filteredNotices.slice(startIndex, startIndex + size);
  }, [filteredNotices, page, size]);

  const totalElements = filteredNotices.length;
  const totalPages = Math.ceil(totalElements / size);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    setPage(0);
  };

  // 필터 변경 시 첫 페이지로
  useEffect(() => {
    setPage(0);
  }, [filterCategory, filterStatus, searchKeyword]);

  const getPriorityColor = (priority) => {
    switch (priority) {
      case '높음': return '#EF4444';
      case '보통': return '#FBBF24';
      case '낮음': return '#4ADE80';
      default: return '#8B92B0';
    }
  };

  return (
    <MainLayout>
      <div className="notice-management-container">
        {/* Header Section */}
        <div className="notice-management-header">
          <div className="notice-management-header-left">
            <div className="notice-management-title">공지사항 현황</div>
            <p className="notice-management-subtitle">시스템 공지 및 안내사항 관리</p>
          </div>
          <div className="notice-management-header-right">
            {/* Optional update info */}
          </div>
        </div>

        <div className="notice-management-content">
          <div className="notice-filters">
            <div className="filters-left">
              <div className="filter-group">
                <label>카테고리</label>
                <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
                  <option value="전체">전체</option>
                  <option value="시스템">시스템</option>
                  <option value="장비">장비</option>
                  <option value="품질">품질</option>
                  <option value="알림설정">알림설정</option>
                  <option value="교육">교육</option>
                  <option value="일반">일반</option>
                </select>
              </div>
              <div className="filter-group">
                <label>상태</label>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="전체">전체</option>
                  <option value="게시중">게시중</option>
                  <option value="대기">대기</option>
                  <option value="예정">예정</option>
                  <option value="비활성">비활성</option>
                  <option value="종료">종료</option>
                </select>
              </div>
            </div>
            <div className="filters-right">
              <div className="search-box">
                <span className="material-symbols-outlined search-icon">search</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="제목, 내용 검색"
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                />
              </div>
              <button className="btn-add-notice" onClick={handleOpenAddModal}>
                <svg className="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                공지사항 등록
              </button>
            </div>
          </div>

          <div className="notice-table-container">
            <table className="notice-table">
            <thead>
              <tr>
                <th style={{width: '3%'}}></th>
                <th style={{width: '8%'}}>카테고리</th>
                <th style={{width: '8%'}}>시작일</th>
                <th style={{width: '8%'}}>종료일</th>
                <th style={{width: '35%'}}>제목</th>
                <th style={{width: '8%'}}>작성자</th>
                <th style={{width: '8%'}}>우선순위</th>
                <th style={{width: '7%'}}>상태</th>
                <th style={{width: '7%'}}>조회수</th>
                <th style={{width: '8%'}}>작성일</th>
              </tr>
            </thead>
            <tbody>
              {paginatedNotices.map((notice) => (
                <tr key={notice.id} onClick={() => handleNoticeClick(notice)}>
                  <td>
                    {notice.isPinned && (
                      <span className="material-symbols-outlined pin-icon">push_pin</span>
                    )}
                  </td>
                  <td>
                    <span className="category-badge">{notice.category}</span>
                  </td>
                  <td>{notice.startDate ? new Date(notice.startDate).toLocaleDateString('ko-KR') : '-'}</td>
                  <td>{notice.endDate ? new Date(notice.endDate).toLocaleDateString('ko-KR') : '-'}</td>
                  <td className="notice-title-cell">
                    <div className="notice-title">{notice.title}</div>
                  </td>
                  <td>{notice.author}</td>
                  <td>
                    <span
                      className="priority-badge"
                      style={{
                        background: `${getPriorityColor(notice.priority)}20`,
                        color: getPriorityColor(notice.priority)
                      }}
                    >
                      {notice.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${notice.status === '게시중' ? 'active' : 'inactive'}`}>
                      {notice.status}
                    </span>
                  </td>
                  <td>
                    <div className="views-count">
                      <span className="material-symbols-outlined">visibility</span>
                      {notice.views}
                    </div>
                  </td>
                  <td>{notice.createdAt ? new Date(notice.createdAt).toLocaleDateString('ko-KR') : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {totalElements > 0 && (
            <Pagination
              page={page}
              totalPages={totalPages}
              totalElements={totalElements}
              size={size}
              onPageChange={handlePageChange}
              onSizeChange={handleSizeChange}
            />
          )}
          </div>
        </div>

        {/* 공지사항 등록 모달 */}
        {isAddModalOpen && (
          <>
            <div className="modal-overlay" onClick={handleCloseAddModal}></div>
            <div className="notice-add-modal">
              <div className="modal-header">
                <h3>공지사항 등록</h3>
                <button className="btn-close" onClick={handleCloseAddModal}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-content">
                <div className="form-section">
                  <label>제목 <span className="required">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="공지사항 제목을 입력하세요"
                    value={newNotice.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>
                <div className="form-section">
                  <label>카테고리</label>
                  <select
                    className="form-select"
                    value={newNotice.category}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                  >
                    <option value="시스템">시스템</option>
                    <option value="장비">장비</option>
                    <option value="품질">품질</option>
                    <option value="알림설정">알림설정</option>
                    <option value="교육">교육</option>
                    <option value="일반">일반</option>
                  </select>
                </div>
                <div className="form-section">
                  <label>우선순위</label>
                  <select
                    className="form-select"
                    value={newNotice.priority}
                    onChange={(e) => handleInputChange('priority', e.target.value)}
                  >
                    <option value="낮음">낮음</option>
                    <option value="보통">보통</option>
                    <option value="높음">높음</option>
                  </select>
                </div>
                <div className="form-section checkbox-section">
                  <label>상단 고정</label>
                  <div className="checkbox-wrapper" onClick={() => handleInputChange('isPinned', !newNotice.isPinned)}>
                    <input
                      type="checkbox"
                      checked={newNotice.isPinned}
                      onChange={(e) => handleInputChange('isPinned', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>헤더에 공지 표시</span>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-section">
                    <label>게시 시작일</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={newNotice.startDate}
                      onChange={(e) => handleInputChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="form-section">
                    <label>게시 종료일</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={newNotice.endDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-section">
                  <label>내용 <span className="required">*</span></label>
                  <textarea
                    className="form-textarea"
                    placeholder="공지사항 내용을 입력하세요"
                    rows="8"
                    value={newNotice.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-primary" onClick={handleAddNotice}>등록</button>
                <button className="btn-secondary" onClick={handleCloseAddModal}>취소</button>
              </div>
            </div>
          </>
        )}

        {/* 공지사항 수정 모달 */}
        {isEditModalOpen && (
          <>
            <div className="modal-overlay" onClick={handleCloseEditModal}></div>
            <div className="notice-add-modal">
              <div className="modal-header">
                <h3>공지사항 수정</h3>
                <button className="btn-close" onClick={handleCloseEditModal}>
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              <div className="modal-content">
                <div className="form-section">
                  <label>제목 <span className="required">*</span></label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="공지사항 제목을 입력하세요"
                    value={editNotice.title}
                    onChange={(e) => handleEditInputChange('title', e.target.value)}
                  />
                </div>
                <div className="form-section">
                  <label>카테고리</label>
                  <select
                    className="form-select"
                    value={editNotice.category}
                    onChange={(e) => handleEditInputChange('category', e.target.value)}
                  >
                    <option value="시스템">시스템</option>
                    <option value="장비">장비</option>
                    <option value="품질">품질</option>
                    <option value="알림설정">알림설정</option>
                    <option value="교육">교육</option>
                    <option value="일반">일반</option>
                  </select>
                </div>
                <div className="form-section">
                  <label>우선순위</label>
                  <select
                    className="form-select"
                    value={editNotice.priority}
                    onChange={(e) => handleEditInputChange('priority', e.target.value)}
                  >
                    <option value="낮음">낮음</option>
                    <option value="보통">보통</option>
                    <option value="높음">높음</option>
                  </select>
                </div>
                <div className="form-section checkbox-section">
                  <label>상단 고정</label>
                  <div className="checkbox-wrapper" onClick={() => handleEditInputChange('isPinned', !editNotice.isPinned)}>
                    <input
                      type="checkbox"
                      checked={editNotice.isPinned}
                      onChange={(e) => handleEditInputChange('isPinned', e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <span>헤더에 공지 표시</span>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-section">
                    <label>게시 시작일</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={editNotice.startDate}
                      onChange={(e) => handleEditInputChange('startDate', e.target.value)}
                    />
                  </div>
                  <div className="form-section">
                    <label>게시 종료일</label>
                    <input
                      type="datetime-local"
                      className="form-input"
                      value={editNotice.endDate}
                      onChange={(e) => handleEditInputChange('endDate', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-section">
                  <label>내용 <span className="required">*</span></label>
                  <textarea
                    className="form-textarea"
                    placeholder="공지사항 내용을 입력하세요"
                    rows="8"
                    value={editNotice.content}
                    onChange={(e) => handleEditInputChange('content', e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn-primary" onClick={handleUpdateNotice}>수정</button>
                <button className="btn-secondary" onClick={handleCloseEditModal}>취소</button>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="공지 삭제"
        message={noticeToDelete ? `"${noticeToDelete.title}" 공지를 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.` : ''}
        confirmText="삭제"
        cancelText="취소"
        type="danger"
      />
    </MainLayout>
  );
}

export default NoticeManagementPage;
